declare const realtimePresenceData: unique symbol;

/**
 * One named slice of the connection's presence document.
 *
 * `decode` is the runtime boundary for metadata received from the realtime server. The optional
 * legacy projection keeps older clients compatible while a slice moves from a flat document to a
 * namespaced one; it can be removed after the transition release.
 */
export type RealtimePresenceDefinition<T> = Readonly<{
  name: string;
  decode: (data: unknown) => T | null;
  legacyProjection?: (data: T) => Record<string, unknown>;
  [realtimePresenceData]?: T;
}>;

export function defineRealtimePresence<T>(
  name: string,
  decode: (data: unknown) => T | null,
  options: { legacyProjection?: (data: T) => Record<string, unknown> } = {},
): RealtimePresenceDefinition<T> {
  return Object.freeze({ name, decode, ...options });
}

/** Who is on the channel, with no slice decoded. Enough for a roster or an avatar list. */
export type RealtimeParticipantIdentity = {
  userId: number;
  connectionId: string;
  isSelf: boolean;
};

export type RealtimeParticipant<T> = RealtimeParticipantIdentity & {
  data: T | null;
};

export type RealtimePresence<T> = {
  participants: RealtimeParticipant<T>[];
  setPresence: (data: T | null) => void;
};
